// "WHAT YOUR CREDIT DID" ON THE NO-CASE RESULT: ITS ROWS, FROM THE SERVER'S FACTS (WEB-DESIGN-1, R234).
//
// Tyler, after walking the test site (2026-10-05): "we need to highlight the benefit of what they just spend 29.99 on
// (search of comps, records, etc) so they dont feel like they got scammed". Decision 5: "the no-case result showing what
// the credit bought".
//
// EVERY FIGURE IS THE SERVER'S, read off responses the Analyze step already holds — nothing is computed here:
//   - the sales: R-3's `compCount`, the figure the no-case card itself shows (NoCaseVerdict.swift:180), and the search's
//     `meta.candidatesConsidered`, how many sales the search found before screening;
//   - the county record: the appeal's `subject` (every response's `appeal` snapshot) — its parcel and assessed value,
//     shown only when the server says the record was ADOPTED (`subject.record.standing`), so a home whose details the owner
//     typed in is never told a record was checked;
//   - the sale history: the subject's `lastSale`, its price and date, only when the server holds one;
//   - the case check: the check the server just answered (R-3 ran and named a shape);
//   - the save: the appeal the server holds, by its `updatedAt`.
//
// THE WORDS. Each row's label is the app's own where the app has one — "sales sourced" (NoCaseVerdict.swift:180),
// "Parcel ID / APN" (SubjectPropertyView.swift:1716), "Assessed value" (:1775), "Sale price" and "Sale date"
// (CompEditView.swift:143, :146), "Honest case check" (AnalyzeView.swift:254). The panel's heading and the row captions
// that say what was done were new claims, drafted for Tyler; he APPROVED them word for word on 2026-10-05 ("Yes ABC, yes
// put on test site", `WALK_COPY` under `WALK_DRAFTS_APPROVED` in `components/heldCopy.ts`, draft B), so every build shows
// them, with the server's figures. One label was drafted but NOT in that approval: "sales found", beside the search's
// `candidatesConsidered`. It stays a DRAFT (`CREDIT_WORK_DRAFTS`): never shipped, shown only on a dev build with
// `?drafts=1`, and a shipped page leaves that figure out.

import type { EvaluateCaseResponse, SearchCompsResponse } from "@contract";
import { money, shortDate } from "../../format";
import type { WalkCopyId } from "../../components/heldCopy";
import { words, type DetailsWordingId } from "./detailsWording";
import { say4, type Parity4WordingId } from "./parity4Wording";

type R3 = EvaluateCaseResponse["decisions"]["R-3"];
export type R3Value = NonNullable<Extract<R3, { ran: true }>["value"]>;

/** The one draft left, word for word as the report gave it to Tyler. NOT APPROVED (2026-10-05's reply approved the
 *  heading and the five captions, not this): rendered only by a dev build with `?drafts=1` (`draftsOn`), so a production
 *  bundle carries none of it. */
export const CREDIT_WORK_DRAFTS = import.meta.env.DEV ? { found: "sales found" } as const : null;

export type CreditWorkRowId = "sales" | "record" | "history" | "check" | "saved";

/** The panel's approved heading, and each row's approved caption (draft B, `WALK_COPY`). */
export const CREDIT_WORK_HEADING = "work.heading" satisfies WalkCopyId;
export const CREDIT_WORK_CAPTIONS = {
  sales: "work.sales",
  record: "work.record",
  history: "work.history",
  check: "work.check",
  saved: "work.saved",
} as const satisfies Record<CreditWorkRowId, WalkCopyId>;

/** One figure and its label. `app`/`details` name an approved app string; `draft` names a draft, never shipped. */
export interface WorkFigure {
  readonly value: string;
  readonly label:
    | { readonly kind: "noCase"; readonly id: Parity4WordingId }
    | { readonly kind: "details"; readonly id: DetailsWordingId }
    | { readonly kind: "draft"; readonly id: "found" };
}

export interface WorkRow {
  /** The row's caption is `CREDIT_WORK_CAPTIONS[id]`, approved: shown on every build. */
  readonly id: CreditWorkRowId;
  readonly figures: readonly WorkFigure[];
}

/** The tick a done step shows in place of a number. A mark, not a word. */
export const DONE_MARK = "✓";

export function labelText(label: WorkFigure["label"]): string | null {
  if (label.kind === "noCase") return say4(label.id);
  if (label.kind === "details") return words(label.id);
  return CREDIT_WORK_DRAFTS === null ? null : CREDIT_WORK_DRAFTS[label.id];
}

/** What a page shows of a row's figures: with drafts off, only those whose label is approved (every row still shows, by
 *  its approved caption). With drafts on, every figure. */
export function shownFigures(row: WorkRow, drafts: boolean): WorkFigure[] {
  return row.figures.filter((f) => drafts || f.label.kind !== "draft");
}

/** The rows, from the server's facts, in the order the work was done. A row whose facts the server did not send is left
 *  out, never shown as done. */
export function creditWorkRows(v: R3Value, comps: SearchCompsResponse, check: EvaluateCaseResponse): WorkRow[] {
  const rows: WorkRow[] = [];
  rows.push({
    id: "sales",
    figures: [
      { value: String(comps.body.meta.candidatesConsidered), label: { kind: "draft", id: "found" } },
      { value: String(v.compCount), label: { kind: "noCase", id: "nocase.metric.sourced" } },
    ],
  });
  const subject = check.appeal?.subject ?? comps.appeal?.subject ?? null;
  if (subject !== null && subject.record?.standing === "adopted") {
    const figures: WorkFigure[] = [];
    if (subject.parcel !== null) figures.push({ value: subject.parcel.value, label: { kind: "details", id: "details.parcel.label" } });
    if (subject.assessedValue !== null) {
      figures.push({ value: money(subject.assessedValue.value), label: { kind: "details", id: "details.value.assessed" } });
    }
    if (figures.length > 0) rows.push({ id: "record", figures });
  }
  if (subject !== null && subject.lastSale !== null) {
    const figures: WorkFigure[] = [{ value: money(subject.lastSale.price), label: { kind: "details", id: "details.sale.price" } }];
    if (subject.lastSale.date !== null) figures.push({ value: shortDate(subject.lastSale.date), label: { kind: "details", id: "details.sale.date" } });
    rows.push({ id: "history", figures });
  }
  rows.push({ id: "check", figures: [{ value: DONE_MARK, label: { kind: "noCase", id: "analyze.card.title" } }] });
  if (check.appeal !== null) rows.push({ id: "saved", figures: [] });
  return rows;
}
